import { cn } from '@/lib/utils';
import { useDEAIChat } from '@/hooks/use-deai-chat';
import { TypingIndicator } from '@/components/typing-indicator';
import { useAppStore } from '@/hooks/use-app-store';
import { useState, useEffect, useRef } from 'react';

export const FortuneAIMessageDisplay = ({ className }: { className?: string }) => {
  const { chatMessages, isTyping } = useDEAIChat();
  const { sub, unsub } = useAppStore();
  const [canShowMessage, setCanShowMessage] = useState(true);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get the most recent AI message
  const aiMessages = chatMessages.filter(msg => msg.role === "assistant");
  const latestAIMessage = aiMessages[aiMessages.length - 1];

  // Track message changes and set up audio synchronization
  useEffect(() => {
    if (latestAIMessage && latestAIMessage.content !== pendingMessage) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // New message detected - hide display until audio is ready
      setCanShowMessage(false);
      setPendingMessage(latestAIMessage.content);

      // Emergency timeout to show message after 10 seconds if no audio
      timeoutRef.current = setTimeout(() => {
        setCanShowMessage(true);
      }, 10000);
    }
  }, [latestAIMessage?.content, pendingMessage]);

  // Subscribe to audio events
  useEffect(() => {
    const handleAudio = () => {
      // Audio received - allow message to be shown
      setCanShowMessage(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    sub('audio', handleAudio);

    return () => {
      unsub('audio', handleAudio);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [sub, unsub]);

  // Determine which message to display
  const displayMessage = canShowMessage ? latestAIMessage : null;

  // Calculate dynamic font size based on message length
  const getDynamicFontSize = (content: string) => {
    if (!content) return 'clamp(0.8rem, 3.5vw, 1.2rem)';

    const length = content.length;

    // Adjust font size based on content length
    if (length < 100) {
      return 'clamp(1rem, 4vw, 1.4rem)'; // Larger for short messages
    } else if (length < 200) {
      return 'clamp(0.9rem, 3.5vw, 1.3rem)'; // Default size
    } else if (length < 350) {
      return 'clamp(0.8rem, 3vw, 1.2rem)'; // Smaller for medium messages
    } else if (length < 500) {
      return 'clamp(0.7rem, 2.5vw, 0.9rem)'; // Smaller for long messages
    } else {
      return 'clamp(0.6rem, 2.2vw, 0.8rem)'; // Smallest for very long messages
    }
  };

  return (
    <div className={cn("w-full mx-auto", className)}>
        <div
          className="relative w-full flex items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/img/omikuji/Omikuji_UI_HEADER_TEXT.png)',
            backgroundSize: 'contain',
            aspectRatio: '2080 / 840',
            minHeight: '120px'
          }}
        >
          <div className="relative flex flex-col items-center justify-center h-full mt-6 px-[18%] py-[15%] overflow-hidden">
            {displayMessage && (
              <div className="text-center bg-transparent leading-tight max-h-full overflow-hidden flex items-end">
                <div className="w-full"
                  style={{
                    fontSize: getDynamicFontSize(displayMessage?.content || ''),
                    lineHeight: '1.2',
                    fontFamily: "'Yuji Syuku', serif"
                  }}
                >
                  {displayMessage?.content}
                </div>
              </div>
            )}
            {/* Gradient fade mask at the top */}
            <div
              className="absolute top-[37%] left-[17%] right-[17%] h-8 pointer-events-none"
              style={{
                background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.4) 30%, rgba(255,255,255,0) 100%)'
              }}
            />
            <TypingIndicator isTyping={isTyping || !canShowMessage} />
          </div>
        </div>
    </div>
  );
};
